import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { Alert, Button, LoadingIndicator } from 'components/common';
import { Form, FormGroup, Select } from 'components/common/form';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import { CalendarEventFeedbackSubmissionQuestion } from '.';

import type {
  GetCalendarEventFeedbackFormRes,
  GetCalendarEventFeedbackTemplatesRes
} from 'shared-types/responses';

interface Props {
  applyTemplateUrl: string;
  disabled?: boolean;
  hasQuestions: boolean;
  tooltip?: string;
  updateQuestions: () => Promise<void>;
}

const CalendarEventFeedbackTemplateApply = ({
  applyTemplateUrl,
  disabled = false,
  hasQuestions,
  tooltip,
  updateQuestions
}: Props) => {
  const { dispatch } = useDispatch();
  const { messages } = useReduxState();

  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState<number | undefined>();

  const { data } = useApiData<GetCalendarEventFeedbackTemplatesRes>({
    url: '/admin/calendar-event-feedback-templates'
  });

  // a title-only template has nothing to copy, so it is never offered here
  const templates = (data?.templates || []).filter(
    (template) => template.numberOfQuestions
  );

  const templateOptions = templates.map((template) => ({
    label: template.title,
    value: template.id
  }));

  const { data: selectedTemplateData, loading: loadingSelectedTemplate } =
    useApiData<GetCalendarEventFeedbackFormRes>({
      onlyIf: Boolean(selectedFormId),
      url: `/admin/calendar-event-feedback-templates/${selectedFormId}`
    });

  const selectedTemplateQuestions = selectedTemplateData?.form?.questions || [];

  const resetAndClose = () => {
    setModalVisible(false);
    setConfirming(false);
    setLoading(false);
    setSelectedFormId(undefined);
  };

  const applyTemplate = async () => {
    try {
      setLoading(true);
      await api.post(applyTemplateUrl, { formId: selectedFormId });
      await updateQuestions();

      resetAndClose();

      dispatch.addNotice({
        text: messages.eventCalendarFeedbackApplyTemplateSuccess,
        type: 'success'
      });
    } catch (err) {
      setLoading(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const handleApplyClick = () => {
    if (!selectedFormId) return;

    if (hasQuestions) setConfirming(true);
    else applyTemplate();
  };

  // nothing to apply - no templates exist yet, or every one of them is title
  // only. this also covers the initial load, so the button appears once there
  // is something behind it rather than opening an empty dropdown
  if (!templates.length) return null;

  return (
    <div className="apply-template">
      <Button
        category="lighter"
        disabled={disabled}
        icon="file-import"
        onClick={() => setModalVisible(true)}
        size="header"
        text={messages.eventCalendarFeedbackApplyTemplate}
        tooltip={disabled ? tooltip : undefined}
      />

      <ModalPortal>
        <Modal
          onHide={resetAndClose}
          title={messages.eventCalendarFeedbackApplyTemplate}
          visible={modalVisible}
          width="medium"
        >
          <ModalContent>
            <Form>
              {() =>
                confirming ? (
                  <Alert
                    text={
                      messages.eventCalendarFeedbackApplyTemplateConfirmMessage
                    }
                    type="warning-light"
                  />
                ) : (
                  <>
                    <FormGroup
                      htmlFor="apply-template-select"
                      label={
                        messages.eventCalendarFeedbackApplyTemplateSelectLabel
                      }
                    >
                      <Select
                        field="formId"
                        id="apply-template-select"
                        isClearable={false}
                        onChange={(value: number) => setSelectedFormId(value)}
                        options={templateOptions}
                      />
                    </FormGroup>

                    {selectedFormId ? (
                      <div className="preview">
                        {loadingSelectedTemplate ? (
                          <LoadingIndicator />
                        ) : (
                          selectedTemplateQuestions.map((question) => (
                            <CalendarEventFeedbackSubmissionQuestion
                              disabled={true}
                              key={question.id}
                              question={question}
                            />
                          ))
                        )}
                      </div>
                    ) : null}
                  </>
                )
              }
            </Form>
          </ModalContent>

          <ModalFooter>
            <Button
              category="light"
              onClick={confirming ? () => setConfirming(false) : resetAndClose}
              text={messages.commonCancel}
            />
            <Button
              category="success"
              disabled={!selectedFormId}
              loading={loading}
              onClick={confirming ? applyTemplate : handleApplyClick}
              text={messages.eventCalendarFeedbackApplyTemplate}
            />
          </ModalFooter>
        </Modal>
      </ModalPortal>

      <style jsx>{`
        .preview {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-top: 20px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFeedbackTemplateApply;
